import { APP_INITIALIZER, LOCALE_ID, NgModule, Optional, PLATFORM_ID, SkipSelf } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
    HTTP_INTERCEPTORS,
    HttpClient,
    provideHttpClient,
    withFetch,
    withInterceptorsFromDi
} from '@angular/common/http';
import { RequestHttpInterceptor } from './interceptor/request.http.interceptor';
import { ResponseHttpInterceptor } from './interceptor/response.http.interceptor';
import { AuthModule } from '../features/auth/auth.module';
import { JwtHttpInterceptor } from './interceptor/jwt.http.interceptor';
import { AccessDeniedComponent } from './pages/access-denied/access-denied.component';
import { SharedModule } from '../shared/shared.module';
import { PageNotFoundComponent } from './pages/page-not-found/page-not-found.component';
import { ErrorComponent } from './pages/error/error.component';
import { ReactiveFormsModule } from '@angular/forms';
import { NavbarModule } from '../shared/navbar/navbar.module';
import { HomeComponent } from '../features/home/home.component';
import { SiteLayoutComponent } from './layout/site/site-layout.component';
import { RouterModule } from '@angular/router';
import { ScrollPanelModule } from 'primeng/scrollpanel';
import { PrivacyComponent } from '../features/privacy/privacy.component';
import { NoAuthLayoutComponent } from '../shared/no-auth-layout/no-auth-layout.component';
import { PrivacyContentComponent } from '../features/privacy/privacy-content/privacy-content.component';
import { LandingComponent } from './layout/landing/landing.component';
import { RankingComponent } from '../features/ranking/ranking.component';
import { AvatarModule } from 'primeng/avatar';
import { LanguageHttpInterceptor } from './interceptor/language.http.interceptor';
import { ScrollTopModule } from 'primeng/scrolltop';
import { CountdownModule } from 'ngx-countdown';
import { Meta } from '@angular/platform-browser';
import { SearchResultsListComponent } from './components/search-results-list/search-results-list.component';
import { SwiperModule } from 'swiper/angular';
import { SkeletonModule } from "primeng/skeleton";
import { ChipModule } from "primeng/chip";
import { TimezoneHttpInterceptor } from "./interceptor/timezone.http.interceptor";
import { NavigationCancelHttpInterceptor } from "./interceptor/navigation-cancel.http.interceptor";
import { FullCalendarModule } from "@fullcalendar/angular";
import { TranslateLoader, TranslateModule, TranslateService } from "@ngx-translate/core";
import { LanguageService } from "./services/language.service";
import { ValidatorConfigService } from "./services/validator-config.service";
import { TranslatedMetaService } from "./services/translated-meta.service";
import { PrimeNGConfig } from "primeng/api";
import { WINDOW_PROVIDERS } from "./providers/window.provider";
import { JWT_OPTIONS, JwtModule } from "@auth0/angular-jwt";
import { StorageService } from "./services/storage.service";
import { NgxPermissionsModule } from "ngx-permissions";
import { StoreModule } from "@ngrx/store";
import { followReducer } from "../state/follow/following.reducer";
import { reactionReducer } from "../state/reaction/reaction.reducer";
import {reactionListsReducer} from '../state/reaction-list/reaction-list.reducer';
import { participantReducer } from "../state/participant/participant.reducer";
import { StoreDevtoolsModule } from "@ngrx/store-devtools";
import { appInitializerFactory, HttpLoaderFactory, jwtOptionsFactory } from "./providers/http-context-providers";
import { TabViewModule } from "primeng/tabview";
import { CacheHttpInterceptor } from "./interceptor/cache.http.interceptor";
import { CacheStoreService } from "./services/cache-store.service";
import { DirectivesModule } from "../shared/directives/directives.module";
import {NetworkStatusService} from "./services/network-status.service";
import {MediaOfflineService} from "./services/media-offline.service";
import {PaginatorModule} from "primeng/paginator";
import {StaticPageComponent} from '../features/static-page/static-page.component';
import {HomeFeedListComponent} from '../shared/feed/home-feed-list/home-feed-list.component';
import {SupportComponent} from '../features/support/support.component';
import {SupportRequestModule} from '../shared/support-request/support-request.module';
import {MobileNotificationsComponent} from './pages/mobile-notifications/mobile-notifications.component';
import {MobileExperienceConfigService} from './mobile/mobile-experience-config.service';
import {NativeSecureSessionService} from './mobile/native-secure-session.service';


@NgModule({
    declarations: [
        AccessDeniedComponent,
        PageNotFoundComponent,
        ErrorComponent,
        HomeComponent,
        RankingComponent,
        SiteLayoutComponent,
        PrivacyComponent,
        NoAuthLayoutComponent,
        PrivacyContentComponent,
        LandingComponent,
        SearchResultsListComponent,
        StaticPageComponent,
        SupportComponent,
        MobileNotificationsComponent
    ],
    imports: [
        CommonModule,
        SharedModule,
        ReactiveFormsModule,
        NavbarModule,
        ScrollPanelModule,
        AuthModule,
        RouterModule,
        AvatarModule,
        ScrollTopModule,
        CountdownModule,
        SwiperModule,
        SkeletonModule,
        ChipModule,
        FullCalendarModule,
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: HttpLoaderFactory,
                deps: [HttpClient, PLATFORM_ID]
            }
        }),
        JwtModule.forRoot({
            jwtOptionsProvider: {
                provide: JWT_OPTIONS,
                useFactory: jwtOptionsFactory,
                deps: [StorageService]
            }
        }),
        NgxPermissionsModule.forRoot(),
        StoreModule.forRoot({
            follows: followReducer,
            reactions: reactionReducer,
            reactionLists: reactionListsReducer,
            participants: participantReducer
        }),
        StoreDevtoolsModule.instrument({
            maxAge: 10,
            logOnly: true, // Restrict extension to log-only mode
            autoPause: true, // Pauses recording actions and state changes when the extension window is not open
        }),
        TabViewModule,
        DirectivesModule,
        PaginatorModule,
        HomeFeedListComponent,
        SupportRequestModule,
    ],
    exports: [
    ],
    providers: [
        provideHttpClient(
            withInterceptorsFromDi(),
            withFetch()
        ),
        {
            provide: HTTP_INTERCEPTORS,
            useClass: LanguageHttpInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: TimezoneHttpInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: RequestHttpInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: NavigationCancelHttpInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: CacheHttpInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: ResponseHttpInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: JwtHttpInterceptor,
            multi: true
        },
        {
            provide: APP_INITIALIZER,
            useFactory: appInitializerFactory,
            deps: [TranslateService, LanguageService, ValidatorConfigService, TranslatedMetaService, PrimeNGConfig, CacheStoreService, NetworkStatusService, MediaOfflineService, MobileExperienceConfigService, NativeSecureSessionService],
            multi: true
        },
        {
            provide: LOCALE_ID,
            useFactory: (languageService: LanguageService) => languageService.getLanguage(),
            deps: [LanguageService]
        },
        WINDOW_PROVIDERS,
        Meta,
    ]
})
export class CoreModule {
    constructor(
        @Optional() @SkipSelf() parentModule: CoreModule,
    ) {
        if (parentModule) {
            throw new Error('CoreModule is already loaded. Import it in the AppModule only');
        }
    }
}
